import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { questionTemplates } from "@/lib/constants/questionDefaults";
import { nonDraggablesTypeSlide } from "@/lib/constants/slides";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useAddQuestionElement } from "@/lib/hooks/slides/editor/useAddQuestionELement";
import { useAppSelector } from "@/store/hooks";
import { backgroundColors } from "@ai-teach/canvas-styles";
import { CompositionRenderer } from "../../editor/renderers/questions/CompositionRenderer";
import { DragMatchingImageRenderer } from "../../editor/renderers/questions/DragMatchingImageRenderer";
import { DragMatchingTextRenderer } from "../../editor/renderers/questions/DragMatchingTextRenderer";
import { FillBlanksRenderer } from "../../editor/renderers/questions/FillBlanksRenderer";
import { ImageChoiceRenderer } from "../../editor/renderers/questions/ImageChoiceRenderer";
import { OddImageOutRenderer } from "../../editor/renderers/questions/OddImageOutRenderer";
import { OddTextOutRenderer } from "../../editor/renderers/questions/OddTextOutRenderer";
import { SpeakingRenderer } from "../../editor/renderers/questions/SpeakingRenderer";
import { SpellingRenderer } from "../../editor/renderers/questions/SpellingRenderer";
import { TextChoiceRenderer } from "../../editor/renderers/questions/TextChoiceRenderer";
import { DisabledLockOverlay } from "./DisabledLockOverlay";

export function QuestionsPanel() {
  const { setActivePanel } = useSlideEditorContext();

  const { handleAddQuestionElement } = useAddQuestionElement();

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  if (!slide) return null;

  const slideType = slide.type;
  const knowledgeArea = slide.knowledgeArea;

  const questionsDisabled = (nonDraggablesTypeSlide[slideType] as readonly string[]).includes(
    knowledgeArea
  );

  const handleClickQuestionElement = (type: string) => {
    handleAddQuestionElement(type);
    setActivePanel(null);
  };

  // Preview-only props (id and zIndex not needed in actual elements)
  const previewProps = {
    id: "preview",
    zIndex: 0,
  };

  const readingQuestionItems = [
    {
      type: "textChoice",
      label: "Text Choice",
      preview: (
        <div className="flex justify-center items-center">
          <div className="w-[60%] aspect-[3/1] relative">
            <TextChoiceRenderer element={{ ...previewProps, ...questionTemplates.textChoice }} />
          </div>
        </div>
      ),
    },
    {
      type: "imageChoice",
      label: "Image Choice",
      preview: (
        <div className="flex justify-center items-center">
          <div className="w-[40%] aspect-square">
            <ImageChoiceRenderer element={{ ...previewProps, ...questionTemplates.imageChoice }} />
          </div>
        </div>
      ),
    },
    {
      type: "dragMatchingText",
      label: "Drag to Match (Text)",
      preview: (
        <div className="w-full flex justify-center items-center aspect-[5/6]">
          <DragMatchingTextRenderer
            element={{
              ...previewProps,
              ...questionTemplates.dragMatchingText,
            }}
          />
        </div>
      ),
    },
    {
      type: "dragMatchingImage",
      label: "Drag to Match (Image)",
      preview: (
        <div className="w-full flex justify-center items-center aspect-[5/6]">
          <DragMatchingImageRenderer
            element={{
              ...previewProps,
              ...questionTemplates.dragMatchingImage,
            }}
          />
        </div>
      ),
    },
    {
      type: "oddTextOut",
      label: "Odd One Out (Text)",
      preview: (
        <div className="w-full flex justify-center items-center aspect-[3/1]">
          <OddTextOutRenderer
            element={{
              ...previewProps,
              ...questionTemplates.oddTextOut,
            }}
          />
        </div>
      ),
    },
    {
      type: "oddImageOut",
      label: "Odd One Out (Image)",
      preview: (
        <div className="w-full flex justify-center items-center aspect-[3/1]">
          <OddImageOutRenderer
            element={{
              ...previewProps,
              ...questionTemplates.oddImageOut,
            }}
          />
        </div>
      ),
    },
    {
      type: "fillBlanks",
      label: "Fill in the Blanks (Choice)",
      preview: (
        <div className="w-full flex justify-center items-center">
          <FillBlanksRenderer
            element={{
              ...previewProps,
              ...questionTemplates.fillBlanks,
            }}
            isLiveMode={true}
          />
        </div>
      ),
    },
    {
      type: "fillBlanksImage",
      label: "Fill in the Blanks (Image, Choice)",
      preview: (
        <div className="w-full flex justify-center items-center">
          <FillBlanksRenderer
            element={{
              ...previewProps,
              ...questionTemplates.fillBlanksImage,
            }}
            isLiveMode={true}
          />
        </div>
      ),
    },
    {
      type: "fillBlanksType",
      label: "Fill in the Blanks (Type)",
      preview: (
        <div className="w-full flex justify-center items-center">
          <FillBlanksRenderer
            element={{
              ...previewProps,
              ...questionTemplates.fillBlanksType,
            }}
            isLiveMode={true}
          />
        </div>
      ),
    },
    {
      type: "fillBlanksImageType",
      label: "Fill in the Blanks (Image, Type)",
      preview: (
        <div className="w-full flex justify-center items-center">
          <FillBlanksRenderer
            element={{
              ...previewProps,
              ...questionTemplates.fillBlanksImageType,
            }}
            isLiveMode={true}
          />
        </div>
      ),
    },
    {
      type: "orderImage",
      label: "Order (Image)",
      preview: (
        <div className="w-full rounded-lg border border-gray-200 overflow-hidden bg-white">
          {/* Header: question media (static preview) */}
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border-b border-gray-200">
            <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-1">
              Question Media
            </span>
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-white border border-gray-200 text-gray-400">
              🎵 Audio
            </span>
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-white border border-gray-200 text-gray-400">
              🎬 Video
            </span>
          </div>
          <div className="flex items-center justify-center py-1 border-b border-gray-100">
            <span className="text-[9px] text-gray-300">
              Optional: add audio or video prompt above
            </span>
          </div>
          {/* Image slots */}
          <div className="flex justify-center items-end gap-3 px-3 py-3">
            {[1, 2, 3].map((n) => (
              <div key={`order-img-${n}`} className="flex flex-col items-center gap-1">
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 flex items-center justify-center">
                  <svg
                    aria-hidden="true"
                    className="w-6 h-6 text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="w-5 h-5 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {n}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      type: "orderText",
      label: "Order (Text)",
      preview: (
        <div className="w-full rounded-lg border border-gray-200 overflow-hidden bg-white">
          {/* Header: question media (static preview) */}
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border-b border-gray-200">
            <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest flex-1">
              Question Media
            </span>
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-white border border-gray-200 text-gray-400">
              🎵 Audio
            </span>
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-white border border-gray-200 text-gray-400">
              🎬 Video
            </span>
          </div>
          <div className="flex items-center justify-center py-1 border-b border-gray-100">
            <span className="text-[9px] text-gray-300">
              Optional: add audio or video prompt above
            </span>
          </div>
          {/* Text items */}
          <div className="flex flex-col gap-1.5 px-2 py-2">
            {["First", "Second", "Third"].map((text, i) => (
              <div
                key={text}
                className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-2 py-1"
              >
                <div className="w-5 h-5 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <span className="text-[11px] font-medium text-gray-600">{text}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      type: "spelling",
      label: "Spelling",
      preview: (
        <div className="flex justify-center items-center">
          <div className="w-full aspect-[3/1] relative">
            <SpellingRenderer
              element={{
                ...previewProps,
                ...questionTemplates.spelling,
              }}
              isLiveMode={true}
            />
          </div>
        </div>
      ),
    },
    {
      type: "speaking",
      label: "Speaking",
      preview: (
        <div className="flex justify-center items-center">
          <div className="w-full aspect-[6/7] relative">
            <SpeakingRenderer
              element={{
                ...previewProps,
                ...questionTemplates.speaking,
              }}
              isLiveMode={true}
            />
          </div>
        </div>
      ),
    },
    {
      type: "composition",
      label: "Composition",
      preview: (
        <div className="flex justify-center items-center">
          <div className="w-full aspect-[9/7] relative">
            <CompositionRenderer
              element={{
                ...previewProps,
                ...questionTemplates.composition,
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="px-2">
      {/* Section header */}
      <div className="mb-4">
        <p className="text-sm font-semibold uppercase tracking-wider px-1">Questions</p>
        <p className="text-xs text-slate-400 mt-1 px-1">Click to add to your slide</p>
      </div>

      <div className={`relative mt-8 ${questionsDisabled ? "max-h-[60vh] overflow-hidden" : ""}`}>
        <div
          className={`grid select-none grid-cols-1 gap-3 transition-all duration-300 ${
            questionsDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
          }`}
        >
          {readingQuestionItems.map((item) => (
            <Dnd
              key={item.type}
              id={`question-${item.type}`}
              data={{
                id: item.type,
                type: "question",
                data: {
                  label: item.label,
                  preview: item.preview,
                  size: questionTemplates[item.type as keyof typeof questionTemplates]?.size,
                },
              }}
            >
              <div
                onClick={() => handleClickQuestionElement(item.type)}
                style={{
                  backgroundColor: backgroundColors.canvasBg,
                }}
                className="group relative select-none overflow-hidden rounded-xl border-2 border-slate-200 hover:border-primary-400 transition-all duration-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm cursor-pointer p-3"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-accent-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                <div className="relative">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block text-left mb-2">
                    {item.label}
                  </span>
                  {/* Previews are display-only — no clicks reach the renderers, no selection. */}
                  <div className="pointer-events-none [&_*]:select-none">{item.preview}</div>
                </div>
              </div>
            </Dnd>
          ))}
        </div>

        {questionsDisabled && (
          <DisabledLockOverlay
            title="Questions Not Available"
            description="This slide type doesn't support question elements"
          />
        )}
      </div>

      <div className="mt-6 p-3 bg-primary-50/50 border border-primary-100 rounded-lg">
        <p className="text-xs text-primary-700 text-center">
          <span className="font-semibold">Pro tip:</span> Click elements to add them to your slide
        </p>
      </div>
    </div>
  );
}
